import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface PopupContextType {
    isPopupOpen: boolean;
    openPopup: (projectName?: string) => void;
    closePopup: () => void;
    projectName: string | null;
}

const PopupContext = createContext<PopupContextType | undefined>(undefined);

export const PopupProvider = ({ children }: { children: ReactNode }) => {
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [projectName, setProjectName] = useState<string | null>(null);

    const openPopup = (name?: string) => {
        setProjectName(name || null);
        setIsPopupOpen(true);
    };

    const closePopup = () => {
        setIsPopupOpen(false);
        setProjectName(null);
    };

    // Auto-open popup after 7 seconds
    useEffect(() => {
        const timer = setTimeout(() => {
            // Only open if it hasn't been opened yet (or checked via local storage if we wanted to be persistent)
            // For now, simple logic: auto open once per session load
            if (!isPopupOpen) {
                setIsPopupOpen(true);
            }
        }, 7000);

        return () => clearTimeout(timer);
    }, []);

    return (
        <PopupContext.Provider value={{ isPopupOpen, openPopup, closePopup, projectName }}>
            {children}
        </PopupContext.Provider>
    );
};

export const usePopup = () => {
    const context = useContext(PopupContext);
    if (context === undefined) {
        throw new Error('usePopup must be used within a PopupProvider');
    }
    return context;
};
